import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Input } from '../components/ui/input';
import { MagnifyingGlass, ArrowsClockwise, PencilSimple, Eye, FilePdf, CaretUp, CaretDown, Funnel, X, Lightning, CheckCircle, DownloadSimple, UploadSimple, Trash, Buildings } from '@phosphor-icons/react';
import LeadEditModal from '../components/leads/LeadEditModal';
import LeadPreviewModal from '../components/leads/LeadPreviewModal';
import LeadImportPreviewModal from '../components/leads/LeadImportPreviewModal';
import BulkEditPanel from '../components/leads/BulkEditPanel';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('ragione_sociale');
  const [sortDir, setSortDir] = useState('asc');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkFields, setBulkFields] = useState({});
  const [showBulk, setShowBulk] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [importStep, setImportStep] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('in_corso');
  const [tabCounts, setTabCounts] = useState({ nuovi: 0, in_corso: 0, archiviati: 0 });
  const [documents, setDocuments] = useState({ system: [], custom: [] });
  const fileInputRef = useRef(null);

  const fetchLeads = useCallback(async () => {
    try {
      const [leadsRes, countsRes] = await Promise.all([
        fetch(`${API}/api/leads?tab=${activeTab}`, { credentials: 'include' }),
        fetch(`${API}/api/leads/tabs-counts`, { credentials: 'include' }),
      ]);
      if (leadsRes.ok) setLeads(await leadsRes.json());
      if (countsRes.ok) setTabCounts(await countsRes.json());
    } catch (e) { toast.error('Errore caricamento leads'); }
    finally { setLoading(false); }
  }, [activeTab]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/documents`, { credentials: 'include' });
      if (res.ok) setDocuments(await res.json());
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchLeads(); fetchDocuments(); }, [fetchLeads, fetchDocuments]);

  const updateLeadCte = async (leadId, cteDoc) => {
    // Optimistic update
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, cte_document: cteDoc } : l));
    try {
      const res = await fetch(`${API}/api/pre-contracts/${leadId}/cte`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cte_document: cteDoc }),
      });
      if (res.ok) {
        toast.success(cteDoc ? 'CTE assegnata' : 'CTE rimossa');
      } else {
        toast.error('Errore aggiornamento CTE');
        fetchLeads();
      }
    } catch (e) { toast.error('Errore aggiornamento CTE'); fetchLeads(); }
  };

  const changeStatus = async (id, newStatus, note = '') => {
    try {
      const res = await fetch(`${API}/api/leads/${id}/status`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, note }),
      });
      if (res.ok) {
        toast.success(`Stato → ${newStatus}`);
        fetchLeads();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Errore');
      }
    } catch { toast.error('Errore di rete'); }
  };

  const archiveLead = async (id, reason = 'manuale') => {
    if (!window.confirm('Archiviare questo lead? Potrai riesumarlo in seguito dal tab Archiviati.')) return;
    const res = await fetch(`${API}/api/leads/${id}/archive`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (res.ok) { toast.success('Lead archiviato'); fetchLeads(); }
    else toast.error('Errore');
  };

  const unarchiveLead = async (id) => {
    const res = await fetch(`${API}/api/leads/${id}/unarchive`, {
      method: 'POST', credentials: 'include',
    });
    if (res.ok) { toast.success('Lead ripristinato'); fetchLeads(); }
    else toast.error('Errore');
  };

  const syncFromPods = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/leads/sync-from-pods`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Sincronizzati: ${data.created} nuovi, ${data.updated} aggiornati (${data.total_pods} POD totali)`);
        fetchLeads();
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore sincronizzazione'); }
    finally { setSyncing(false); }
  };

  const saveLead = async (id) => {
    try {
      const res = await fetch(`${API}/api/leads/${id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });
      if (res.ok) {
        toast.success('Lead aggiornato');
        setEditingId(null);
        setEditData({});
        fetchLeads();
      } else toast.error('Errore salvataggio');
    } catch (e) { toast.error('Errore di rete'); }
  };

  const bulkUpdate = async () => {
    const ids = [...selectedIds];
    const fields = Object.fromEntries(Object.entries(bulkFields).filter(([, v]) => v !== ''));
    if (!ids.length || !Object.keys(fields).length) {
      toast.error('Seleziona leads e compila almeno un campo');
      return;
    }
    try {
      const res = await fetch(`${API}/api/leads/bulk-update`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: ids, fields })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.modified} leads aggiornati`);
        setBulkFields({});
        setSelectedIds(new Set());
        fetchLeads();
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore bulk update'); }
  };

  const generatePdf = async (id) => {
    try {
      const res = await fetch(`${API}/api/leads/${id}/generate-pdf`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        const msg = data.is_multisito
          ? `Generati ${data.files.length} PDF (contratto + allegato multisito) per gruppo di ${data.group_size} POD`
          : `PDF generato: ${data.files[0]?.filename}`;
        toast.success(msg);
        fetchLeads();
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore generazione PDF'); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Eliminare questo lead?')) return;
    try {
      const res = await fetch(`${API}/api/leads/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Lead eliminato'); fetchLeads(); }
      else toast.error('Errore eliminazione');
    } catch (e) { toast.error('Errore di rete'); }
  };

  const deleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Eliminare ${ids.length} leads selezionati?`)) return;
    try {
      const res = await fetch(`${API}/api/leads/delete-batch`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: ids })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.deleted} leads eliminati`);
        setSelectedIds(new Set());
        fetchLeads();
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore eliminazione batch'); }
  };

  const [genProgress, setGenProgress] = useState(null);

  const pollProgress = async (taskId) => {
    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/leads/generation-progress/${taskId}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (data.status === 'running') {
          setGenProgress(data);
          setTimeout(poll, 2000);
        } else {
          setGenProgress(null);
          setGenerating(false);
          if (data.status === 'completed') {
            toast.success(`Completato: ${data.success} generati, ${data.failed} errori`);
          } else {
            toast.error('Errore nella generazione');
          }
          fetchLeads();
        }
      } catch { setTimeout(poll, 3000); }
    };
    poll();
  };

  const generateBatch = async () => {
    // Filter selectedIds to only those still visible in `leads` (purges stale ids
    // from previous tabs / removed leads — fixes "selezionati 136 ma totali 133").
    const visibleIds = new Set(leads.map(l => l.id));
    const cleanIds = [...selectedIds].filter(id => visibleIds.has(id));
    if (cleanIds.length !== selectedIds.size) {
      setSelectedIds(new Set(cleanIds));
    }
    const ids = cleanIds;
    const isAll = ids.length === 0;
    if (isAll && !window.confirm(`Generare PDF per tutti i ${leads.length} clienti?\nL'operazione viene eseguita in background.`)) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/leads/generate-batch`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: ids }),
      });
      const data = await res.json();
      if (res.ok && data.task_id) {
        toast.info(data.message);
        setGenProgress({ status: 'running', total: data.total, done: 0, success: 0, failed: 0 });
        pollProgress(data.task_id);
      } else {
        toast.error(data.detail || 'Errore');
        setGenerating(false);
      }
    } catch (e) { toast.error('Errore generazione'); setGenerating(false); }
  };

  const previewContract = async (id) => {
    try {
      const res = await fetch(`${API}/api/leads/${id}/preview-html`, { credentials: 'include' });
      if (res.ok) setPreviewHtml(await res.text());
      else toast.error('Errore anteprima');
    } catch (e) { toast.error('Errore di rete'); }
  };

  const downloadPdf = (id) => window.open(`${API}/api/leads/${id}/download-pdf`, '_blank');
  const exportExcel = () => window.open(`${API}/api/leads/export-excel`, '_blank');

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/api/leads/import-preview`, {
        method: 'POST', credentials: 'include', body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setImportPreview(data);
        setImportStep('preview');
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore upload file'); }
    finally { setImporting(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const confirmImport = async () => {
    if (!importFile) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    try {
      const res = await fetch(`${API}/api/leads/import-confirm`, {
        method: 'POST', credentials: 'include', body: formData
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Import completato: ${data.updated} aggiornati, ${data.created} nuovi`);
        setImportStep(null);
        setImportPreview(null);
        setImportFile(null);
        fetchLeads();
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore conferma import'); }
    finally { setImporting(false); }
  };

  const regenerateAll = async () => {
    if (!window.confirm(`Rigenerare tutti i PDF?\nL'operazione viene eseguita in background.`)) return;
    setRegenerating(true);
    try {
      const res = await fetch(`${API}/api/leads/regenerate-all`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok && data.task_id) {
        toast.info(data.message);
        setGenProgress({ status: 'running', total: data.total, done: 0, success: 0, failed: 0 });
        pollProgress(data.task_id);
      } else toast.error(data.detail || 'Errore');
    } catch (e) { toast.error('Errore rigenerazione'); }
    finally { setRegenerating(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(l => l.id)));
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let result = leads;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(l =>
        (l.ragione_sociale || '').toLowerCase().includes(s) ||
        (l.pods || []).some(p => (p.pod_code || '').toLowerCase().includes(s)) ||
        (l.partita_iva || '').includes(s) ||
        (l.codice_fiscale || '').includes(s)
      );
    }
    result.sort((a, b) => {
      const va = (a[sortKey] || '').toString().toLowerCase();
      const vb = (b[sortKey] || '').toString().toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return result;
  }, [leads, search, sortKey, sortDir]);

  const SortIcon = ({ field }) => {
    if (sortKey !== field) return null;
    return sortDir === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="leads-title">Leads</h1>
          <p className="text-sm text-slate-500">{leads.length} leads totali - {selectedIds.size} selezionati</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={syncFromPods} disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="sync-pods-btn">
            <ArrowsClockwise size={16} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Sincronizzazione...' : 'Importa da POD Dogane'}
          </button>
          <button onClick={exportExcel}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-md text-sm font-medium transition-colors"
            data-testid="export-excel-btn">
            <DownloadSimple size={16} /> Export Excel
          </button>
          <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors cursor-pointer"
            data-testid="import-excel-btn">
            <UploadSimple size={16} /> Import Excel
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportFile} className="hidden" />
          </label>
          <button onClick={() => setShowBulk(!showBulk)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${showBulk ? 'bg-slate-800 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
            data-testid="bulk-edit-toggle">
            <Funnel size={16} /> Modifica Massiva
          </button>
          <button onClick={generateBatch} disabled={generating}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="generate-all-btn">
            <FilePdf size={16} />
            {generating ? 'Generazione...' : selectedIds.size > 0 ? `Genera PDF (${selectedIds.size})` : 'Genera Tutti PDF'}
          </button>
          <button onClick={regenerateAll} disabled={regenerating}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="regenerate-all-btn">
            <ArrowsClockwise size={16} className={regenerating ? 'animate-spin' : ''} />
            {regenerating ? 'Rigenerazione...' : 'Rigenera PDF'}
          </button>
          {selectedIds.size > 0 && (
            <button onClick={deleteSelected}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md text-sm font-medium transition-colors"
              data-testid="delete-selected-btn">
              <Trash size={16} /> Elimina ({selectedIds.size})
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {genProgress && genProgress.status === 'running' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="gen-progress">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-blue-800">
              Generazione PDF in corso: {genProgress.done}/{genProgress.total}
              {genProgress.failed > 0 && <span className="text-red-600 ml-2">({genProgress.failed} errori)</span>}
            </span>
            <span className="text-xs text-blue-600">{genProgress.total > 0 ? Math.round((genProgress.done / genProgress.total) * 100) : 0}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${genProgress.total > 0 ? (genProgress.done / genProgress.total) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      {showBulk && (
        <BulkEditPanel
          selectedCount={selectedIds.size}
          bulkFields={bulkFields}
          setBulkFields={setBulkFields}
          onApply={bulkUpdate}
          onClose={() => setShowBulk(false)}
        />
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b" data-testid="leads-tabs">
        {[
          { id: 'nuovi', label: 'Nuovi', icon: Lightning, color: 'text-orange-600' },
          { id: 'in_corso', label: 'In corso', icon: ArrowsClockwise, color: 'text-blue-600' },
          { id: 'archiviati', label: 'Archiviati', icon: Trash, color: 'text-slate-500' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition ${
                    activeTab === t.id
                      ? 'border-orange-500 text-orange-600 font-semibold'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}>
            <t.icon size={14} weight={activeTab === t.id ? 'bold' : 'regular'} />
            {t.label}
            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${
              activeTab === t.id ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'
            }`}>{tabCounts[t.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca per nome, POD, P.IVA..."
          className="pl-9 h-9 text-sm" data-testid="leads-search" />
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-auto bg-white" style={{maxHeight: '65vh'}}>
        <Table>
          <TableHeader className="sticky top-0 bg-white z-10">
            <TableRow className="border-b-2">
              <TableHead className="w-10">
                <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0}
                  onChange={toggleAll} className="accent-orange-500" data-testid="select-all" />
              </TableHead>
              <TableHead className="cursor-pointer text-xs" onClick={() => handleSort('ragione_sociale')}>
                Ragione Sociale <SortIcon field="ragione_sociale" />
              </TableHead>
              <TableHead className="text-xs">POD</TableHead>
              <TableHead className="text-xs">P.IVA</TableHead>
              <TableHead className="text-xs">Tipo Uso</TableHead>
              <TableHead className="text-xs">CTE</TableHead>
              <TableHead className="text-xs">Agenzia</TableHead>
              <TableHead className="text-xs">Pagamento</TableHead>
              <TableHead className="text-xs">Fonte</TableHead>
              <TableHead className="text-xs">Stato</TableHead>
              <TableHead className="text-xs text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(lead => (
              <TableRow key={lead.id} className={`text-xs hover:bg-slate-50 ${selectedIds.has(lead.id) ? 'bg-orange-50' : ''}`}>
                <TableCell>
                  <input type="checkbox" checked={selectedIds.has(lead.id)} onChange={() => toggleSelect(lead.id)}
                    className="accent-orange-500" data-testid={`select-${lead.id}`} />
                </TableCell>
                <TableCell className="font-medium text-slate-900 max-w-[180px] truncate">
                  <a href={`/leads/${lead.id}`} className="hover:text-orange-600 hover:underline cursor-pointer"
                     onClick={(e) => { e.preventDefault(); window.location.href = `/leads/${lead.id}`; }}>
                    {lead.ragione_sociale}
                  </a>
                </TableCell>
                <TableCell>
                  {(lead.pods || []).length > 1 ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-medium">
                      <Buildings size={10} /> {(lead.pods || []).length} siti
                    </span>
                  ) : (
                    <span className="font-mono text-slate-600 text-[10px]">{(lead.pods || [])[0]?.pod_code || lead.pod_code || '--'}</span>
                  )}
                </TableCell>
                <TableCell className="text-slate-600">{lead.partita_iva}</TableCell>
                <TableCell>{lead.tipologia_uso}</TableCell>
                <TableCell>
                  <select
                    value={lead.cte_document || ''}
                    onChange={(e) => updateLeadCte(lead.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className={`text-[10px] border rounded px-1.5 py-0.5 max-w-[160px] truncate ${
                      lead.cte_document ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-white text-slate-400 border-slate-200'
                    }`}
                    data-testid={`lead-cte-select-${lead.id}`}>
                    <option value="">— Seleziona CTE —</option>
                    {[
                      ...documents.system.filter(d => d.type === 'cte'),
                      ...documents.custom.filter(d => d.type === 'cte'),
                    ].map(d => (
                      <option key={d.filename} value={d.filename}>{d.label}</option>
                    ))}
                  </select>
                </TableCell>
                <TableCell className="text-slate-600">{lead.codice_agenzia || <span className="text-slate-300">--</span>}</TableCell>
                <TableCell className="text-slate-600">{lead.metodo_pagamento || <span className="text-slate-300">--</span>}</TableCell>
                <TableCell>
                  {lead.fonte === 'dogane' && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">
                      <Lightning size={10} /> POD Dogane
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <select value={lead.status || 'verificato'}
                          onChange={(e) => changeStatus(lead.id, e.target.value)}
                          data-testid={`status-select-${lead.id}`}
                          className={`text-[10px] border rounded px-1 py-0.5 ${
                            lead.status === 'attivo' ? 'bg-emerald-50 text-emerald-700' :
                            lead.status === 'firmato' ? 'bg-green-50 text-green-700' :
                            lead.status === 'precontratto' ? 'bg-orange-50 text-orange-700' :
                            lead.status === 'fattura_analizzata' ? 'bg-purple-50 text-purple-700' :
                            lead.status === 'contattato' ? 'bg-blue-50 text-blue-700' :
                            lead.status === 'archiviato' ? 'bg-slate-100 text-slate-500' :
                            'bg-amber-50 text-amber-700'
                          }`}>
                    <option value="verificato">Verificato</option>
                    <option value="contattato">Contattato</option>
                    <option value="fattura_analizzata">Fattura Analizzata</option>
                    <option value="precontratto">Precontratto</option>
                    <option value="firmato">Firmato</option>
                    <option value="attivo">Attivo</option>
                    <option value="archiviato">Archiviato</option>
                  </select>
                  {lead.last_contact_at && (
                    <div className="text-[9px] text-slate-400 mt-0.5">
                      Contatto: {new Date(lead.last_contact_at).toLocaleDateString('it-IT')}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => { setEditingId(lead.id); setEditData({...lead}); }}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-orange-600 transition-colors"
                      title="Modifica" data-testid={`edit-${lead.id}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button onClick={() => previewContract(lead.id)}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors"
                      title="Anteprima Contratto" data-testid={`preview-${lead.id}`}>
                      <Eye size={14} />
                    </button>
                    {lead.pdf_generated ? (
                      <button onClick={() => downloadPdf(lead.id)}
                        className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600 transition-colors"
                        title="Scarica PDF" data-testid={`download-${lead.id}`}>
                        <FilePdf size={14} />
                      </button>
                    ) : (
                      <button onClick={() => generatePdf(lead.id)}
                        className="p-1.5 rounded hover:bg-orange-50 text-orange-500 transition-colors"
                        title="Genera PDF" data-testid={`gen-pdf-${lead.id}`}>
                        <FilePdf size={14} />
                      </button>
                    )}
                    {activeTab === 'archiviati' ? (
                      <button onClick={() => unarchiveLead(lead.id)}
                        className="p-1.5 rounded hover:bg-green-50 text-green-600 transition-colors"
                        title="Ripristina lead" data-testid={`unarchive-${lead.id}`}>
                        <ArrowsClockwise size={14} />
                      </button>
                    ) : (
                      <button onClick={() => archiveLead(lead.id)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                        title="Archivia lead" data-testid={`archive-${lead.id}`}>
                        <Trash size={14} weight="regular" />
                      </button>
                    )}
                    <button onClick={() => deleteLead(lead.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                      title="Elimina" data-testid={`delete-${lead.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-12 text-slate-400">
                  {leads.length === 0 ? 'Nessun lead. Clicca "Importa da POD Dogane" per iniziare.' : 'Nessun risultato per la ricerca.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {editingId && (
        <LeadEditModal
          editData={editData}
          setEditData={setEditData}
          onClose={() => setEditingId(null)}
          onSave={() => saveLead(editingId)}
        />
      )}

      {previewHtml && (
        <LeadPreviewModal previewHtml={previewHtml} onClose={() => setPreviewHtml(null)} />
      )}

      {importStep === 'preview' && importPreview && (
        <LeadImportPreviewModal
          importPreview={importPreview}
          importing={importing}
          onClose={() => { setImportStep(null); setImportPreview(null); }}
          onConfirm={confirmImport}
        />
      )}
    </div>
  );
}
